import { ICommit } from "@/shared-models/ICommit";
import CreditLineTypes from "@/apps/credit-line/store/types";
import {
  ICardInfo,
  ICreditLineDetails,
  IPaymentSchedule,
  ITransaction,
  IWithdrawal,
} from "../models/CreditLineDetails";
import { IOffer } from "@/apps/credit-line/models/IOffer";
import TransactionFilter from "@/apps/credit-line/types/TransactionFilter";
import { IState } from "@/apps/credit-line/models/IState";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { Service } from "@/apps/credit-line/services/network.service";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { ActionContext, ActionTree, Commit, Dispatch } from "vuex";
import { IVuexState } from "@/shared-models/IVuex";
import { IWithdrawalEstimate } from "@/apps/credit-line/models/IWithdrawalEstimate";
import { WithdrawMethod } from "@/apps/credit-line/types/withdrawMethod";
import { areValidationRequired } from "@/apps/credit-line/services/kyc";
import { NationalIdValidationResponse } from "@/apps/credit-line/models/IKycValidations";
import { KycUserFlow } from "../enums/kycEnums";
import { TransactionViewState } from "@/apps/credit-line/types/TransactionViewState";

/**
 * ACTIONS
 */
const actions: ActionTree<IState, IVuexState> = {
  setPaymentSchedule: (
    { commit }: ICommit,
    payload: Array<IPaymentSchedule>
  ): void => {
    commit(CreditLineTypes.SET_PAYMENT_SCHEDULE, payload);
  },
  setPendingWithdrawal: ({ commit }: ICommit, payload: IWithdrawal): void => {
    commit(CreditLineTypes.SET_PENDING_WITHDRAWAL, payload);
  },
  setWithdrawals: ({ commit }: ICommit, payload: IWithdrawal[]): void => {
    commit(CreditLineTypes.SET_WITHDRAWALS, payload);
  },
  setCurrency: ({ commit }: ICommit, payload: string): void => {
    commit(CreditLineTypes.SET_CURRENCY, payload);
  },
  setOffer: ({ commit }: ICommit, payload: IOffer): void => {
    commit(CreditLineTypes.SET_OFFER, payload);
  },
  setOfferUnavailable: ({ commit }: ICommit, payload: IOffer): void => {
    commit(CreditLineTypes.SET_OFFER_UNAVAILABLE, payload);
  },
  setTransactionsFilters: (
    { commit }: ICommit,
    payload: TransactionFilter[]
  ): void => {
    commit(CreditLineTypes.SET_TRANSACTIONS_FILTERS, payload);
  },
  setTransactionsList: ({ commit }: ICommit, payload: ITransaction[]): void => {
    commit(CreditLineTypes.SET_TRANSACTIONS_LIST, payload);
  },
  setSelectedWithdrawMethod: (
    { commit }: ICommit,
    payload: WithdrawMethod | null
  ): void => {
    commit(CreditLineTypes.SET_SELECTED_WITHDRAW_METHOD, payload);
  },
  setWithdrawalEstimate: (
    { commit }: ICommit,
    payload: IWithdrawalEstimate | null
  ): void => {
    commit(CreditLineTypes.SET_WITHDRAWAL_ESTIMATE, payload);
  },

  // Credit card
  fetchSummary: async ({
    commit,
  }: {
    commit: Commit;
    dispatch: Dispatch;
  }): Promise<void> => {
    const CreditLineService = new Service();

    const response = await CreditLineService.getCreditLineSummary({
      deviceTag: AuthenticationHelper.getDeviceTag() as string,
    });

    commit(CreditLineTypes.INIT_SUMMARY, response.data as ICreditLineDetails);
  },

  executeServerRequestedActions: async ({
    state,
    dispatch,
    commit,
  }: {
    state: IState;
    dispatch: Dispatch;
    commit: Commit;
  }): Promise<void> => {
    if (
      state.summary?.serverRequestedActions?.includes(
        "CreateVirtualCardOnDemand"
      )
    ) {
      await dispatch("createVirtualCreditCard", true);

      commit("CLEAR_SERVER_REQUESTED_ACTION", "CreateVirtualCardOnDemand");
    }
  },

  setCardStatus: async (
    { commit, state }: { commit: Commit; state: IState },
    { lastFour, status }: { lastFour: string; status: PayJoyCardStatus }
  ): Promise<void> => {
    const CreditLineService = new Service();

    const response = await CreditLineService.toggleCcStatus({
      deviceTag: AuthenticationHelper.getDeviceTag() as string,
      creditLineId: state.summary?.creditLineId as number,
      status,
      lastFour: lastFour,
    });

    if (response.data.status) {
      commit(CreditLineTypes.SET_CARD_STATUS, {
        lastFour,
        status: response.data.status,
      });
    }
  },
  deleteVirtualCard: async (
    { commit, state }: { commit: Commit; state: IState },
    lastFour: string
  ): Promise<void> => {
    const CreditLineService = new Service();

    const res = await CreditLineService.deleteVirtualCreditCard({
      deviceTag: AuthenticationHelper.getDeviceTag() as string,
      creditLineId: state.summary?.creditLineId + "",
      lastFour,
    });

    if (res.data.status) {
      commit("REMOVE_CREDIT_CARD", lastFour);
    }
  },
  createVirtualCreditCard: async (
    { commit, state, getters }: ActionContext<IState, IVuexState>,
    isServerRequestedAction = false
  ): Promise<void> => {
    const CreditLineService = new Service();

    const params = {
      deviceTag: AuthenticationHelper.getDeviceTag() as string,
      creditLineId: state.summary?.creditLineId as number,
      isServerRequestedAction: isServerRequestedAction,
      cohortName: undefined,
    };

    if (isServerRequestedAction) {
      params.cohortName = getters.getDeviceCohort;
    }

    const res = await CreditLineService.createVirtualCreditCard(params);

    if (res.data.last_four) {
      commit("ADD_CREDIT_CARD", {
        status: PayJoyCardStatus.ACTIVE,
        type: "VIRTUAL",
        lastFour: res.data.last_four,
      } as ICardInfo);
    }
  },
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  addShipmentLog: ({ commit }, message: Record<string, any>): void => {
    commit("ADD_SHIPMENT_LOG", message);
  },
  clearShipmentLogs: ({ commit }): void => {
    commit("CLEAR_SHIPMENT_LOGS");
  },
  fetchWithdrawalEstimate: async (
    { commit }: { commit: Commit },
    { amount }: { amount: number }
  ): Promise<IWithdrawalEstimate> => {
    const CreditLineService = new Service();

    const response = await CreditLineService.getWithdrawalEstimate({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      amount,
    });

    commit(CreditLineTypes.SET_WITHDRAWAL_ESTIMATE, response.data);
    return response.data;
  },
  clearWithdrawalEstimate: ({ commit }: ICommit): void => {
    commit(CreditLineTypes.SET_WITHDRAWAL_ESTIMATE, null);
  },
  fetchValidationRequired: async (
    { commit }: { commit: Commit },
    {
      key,
      reference,
      deviceTag,
    }: {
      key: number;
      reference: KycUserFlow;
      deviceTag: string;
    }
  ): Promise<boolean> => {
    try {
      const res = await areValidationRequired({
        creditLineId: key,
        userFlow: reference,
        deviceTag,
      });
      commit(CreditLineTypes.SET_WITHDRAWAL_VALIDATION, res.data);
      return Boolean(res?.data?.requiredValidations);
    } catch (err) {
      return false;
    }
  },
  setCurpValidationResult(
    { commit }: ActionContext<IState, IVuexState>,
    curpValidationResult: NationalIdValidationResponse
  ) {
    commit(CreditLineTypes.SET_CURP_VALIDATION_RESULT, curpValidationResult);
  },
  setValidationSuccess(
    { commit }: ActionContext<IState, IVuexState>,
    validationSuccess: boolean | null
  ) {
    commit(CreditLineTypes.SET_VALIDATION_SUCCESS, validationSuccess);
  },
  setCurrentTransactionViewState(
    { commit }: ActionContext<IState, IVuexState>,
    viewState: TransactionViewState
  ) {
    commit(CreditLineTypes.SET_CURRENT_TRANSACTION_VIEW_STATE, viewState);
  },
  setSelectedTransaction(
    { commit }: ActionContext<IState, IVuexState>,
    transaction: ITransaction | null
  ) {
    commit(CreditLineTypes.SET_SELECTED_TRANSACTION, transaction);
  },
  resetTransactionView({ commit }: ActionContext<IState, IVuexState>) {
    commit(CreditLineTypes.RESET_TRANSACTION_VIEW);
  },
};

export default actions;
